import { MouseEventHandler, useMemo } from 'react'

import classNames from 'classnames'

import { CommonSuspendedIcon } from '@local/assets/icons'
import { isNumber } from '@local/utils'

import Clickable from '@web/components/Clickable'
import { gql } from '@web/gql'
import {
  ReviewTab_ReviewGroupCard_ReviewGroupFragment,
  UserReviewGroupsInput,
  ReviewGroupExposeStatus,
} from '@web/gql/graphql'
import { useGoTo } from '@web/hooks/useGoTo'
import usePageInfo from '@web/hooks/usePageInfo'
import { extractByTypename } from '@web/libs/apollo/typeGuard'
import { IS_DEV } from '@web/libs/constants'
import routes from '@web/libs/constants/routes'
import { ContentProvider } from '@web/libs/contexts/ContentContext'
import nClicks from '@web/libs/nlog/click/codes'
import { generateIntegerByKey } from '@web/libs/utils'

import { USER_REVIEWGROUP_FILTER_DEFAULT } from '../filter.state'
import Place from './Place'
import styles from './ReviewGroupCard.module.scss'
import { ReviewKeyword, ReviewMedia, ReviewText, ReviewRating } from './ReviewGroupCardContent'

type Props = {
  reviewGroup: ReviewTab_ReviewGroupCard_ReviewGroupFragment
  filter: UserReviewGroupsInput
}

const ReviewGroupCard = ({ reviewGroup, filter }: Props) => {
  const { isMyPage, pageOwner } = usePageInfo()
  const clickCodeArea = isMyPage ? nClicks.reviewTab : nClicks.reviewTabOthers

  const { goTo } = useGoTo()
  const goToReviewFeed: MouseEventHandler<HTMLButtonElement> = event => {
    if (!pageOwner?.id) return

    if (event.ctrlKey && IS_DEV) {
      goTo(routes.REVIEW_DETAIL, {
        params: { reviewGroupId: reviewGroup.id },
      })
    } else {
      const { mediaFilter, sort, placeCategoryId, regionCode } = filter

      goTo(routes.REVIEW_FEED, {
        params: { userId: pageOwner.id },
        query: {
          reviewId: reviewGroup.id,
          mediaFilter: mediaFilter ?? USER_REVIEWGROUP_FILTER_DEFAULT.mediaFilter,
          sort: sort ?? USER_REVIEWGROUP_FILTER_DEFAULT.sort,
          ...(placeCategoryId && { placeCategoryId }),
          ...(regionCode && { regionCode }),
        },
      })
    }
  }

  const stylesBgIndex = useMemo(() => generateIntegerByKey({ key: reviewGroup.id, min: 1, max: 6 }), [reviewGroup.id])

  const { coverMediaItems, text, votedKeywords, rating } = reviewGroup

  const { ImageItem, VideoItem } = extractByTypename(coverMediaItems[0])
  const isMediaReview = !!ImageItem?.url || !!VideoItem?.thumbnail
  const isTextReview = !isMediaReview && !!text
  const isKeywordReview = !isMediaReview && !isTextReview && votedKeywords.length > 0
  const isRatingReview = !isMediaReview && !isTextReview && !isKeywordReview && isNumber(rating)
  const isSuspendedReview =
    reviewGroup &&
    [ReviewGroupExposeStatus.UnexposeTemporarily, ReviewGroupExposeStatus.UnexposePermanently].includes(
      reviewGroup.exposeStatus,
    )

  return (
    <ContentProvider id={reviewGroup.id} type={reviewGroup.__typename}>
      {!isSuspendedReview && (
        <li
          className={classNames(styles.list_item, {
            [styles[`type_bg${stylesBgIndex}`]]: !isMediaReview,
            [styles.type_img]: isMediaReview,
            [styles.type_txt]: isTextReview,
            [styles.type_keyword]: isKeywordReview,
            [styles.type_rating]: isRatingReview,
          })}
        >
          <Clickable
            className={styles.list_item_inner}
            onClick={goToReviewFeed}
            nClickCode={isMediaReview ? clickCodeArea.MEDIARV : clickCodeArea.NOMEDIARV}
          >
            {isMediaReview && <ReviewMedia reviewGroup={reviewGroup} />}
            {isTextReview && <ReviewText reviewGroup={reviewGroup} />}
            {isKeywordReview && <ReviewKeyword reviewGroup={reviewGroup} />}
            {isRatingReview && <ReviewRating reviewGroup={reviewGroup} />}
            <Place reviewGroup={reviewGroup} />
          </Clickable>
        </li>
      )}
      {/* 게시중단 */}
      {isSuspendedReview && (
        <Clickable
          className={classNames(styles.list_item, styles.type_suspended)}
          onClick={goToReviewFeed}
          nClickCode={isMediaReview ? clickCodeArea.MEDIARV : clickCodeArea.NOMEDIARV}
        >
          <div className={styles.list_item_inner}>
            <div className={styles.img_area}>
              <CommonSuspendedIcon className={styles.icon_suspended} aria-hidden="true" />
              <span className="place_blind">게시중단</span>
            </div>
          </div>
        </Clickable>
      )}
    </ContentProvider>
  )
}

export default ReviewGroupCard

ReviewGroupCard.fragments = gql(`
  fragment ReviewTab_ReviewGroupCard_ReviewGroup on ReviewGroup {
    id
    text
    rating
    votedKeywords {
      code
      icon
      text {
        korean
      }
    }
    exposeStatus
    coverMediaItems: mediaItems (limit: 1) {
      id
      type
      isCover
      ... on ImageItem {
        url
      }
      ... on VideoItem {
        thumbnail
        ...ReviewGroupVideoCard_VideoItem
      }
    }
    place {
      id
    }
    selectedProof {
      id
      type
    }
    ...ReviewTab_ReviewGroupCard_Place_ReviewGroup
  }
`)
